// pages/dashboard/Wishlist.jsx
import { HeartOff, ShoppingCart, Trash2 } from "lucide-react";
import { useWishlist } from "../../context/WishlistContext";
import { useCart } from "../../context/CartContext";
import { useEffect, useState } from "react";
import Banner from "../../components/Banner";
import { useAuth } from "../../context/AuthContext";

const Wishlist = () => {
  const {
    wishlist = [],
    removeFromWishlist,
    fetchWishlist,
    clearWishlist,
  } = useWishlist();

  const {
    selectedFlowers,
    selectedAccessories,
    selectedVips,
    addFlower,
    addAccessory,
    addVip,
  } = useCart();

  const [banner, setBanner] = useState({ message: "", type: "success" });

  const [hasFetched, setHasFetched] = useState(false);
  const { user, logout, loading } = useAuth();

  const showBanner = (message, type = "success") => {
    setBanner({ message, type });
    setTimeout(() => setBanner({ message: "", type }), 3000); // auto hide after 3s
  };

  useEffect(() => {
    if (!loading && !user) {
      logout(); // optional: clear memory if server session ended
      window.location.href = "/signin";
      return;
    }

    if (!hasFetched && user) {
      fetchWishlist();
      setHasFetched(true);
    }
  }, [loading, user, hasFetched, fetchWishlist, logout]);

  const handleAddToCart = (item) => {
    // FLOWERS
    if (item.category === "flower") {
      const exists = selectedFlowers.some(f => f._id === item._id);
      if (exists) {
        showBanner("Flower is already in cart.", "warning");
        return;
      }

      addFlower(item);
      showBanner("Flower added to cart", "success");
      return;
    }

    // ACCESSORIES
    if (item.category === "accessory") {
      const exists = selectedAccessories.some(a => a._id === item._id);
      if (exists) {
        showBanner("Accessory is already in cart.", "warning");
        return;
      }

      addAccessory(item);
      showBanner("Accessory added to cart", "success");
      return;
    }

    // VIP ITEMS
    if (item.category === "vip") {
      const exists = selectedVips.some(v => v._id === item._id);
      if (exists) {
        showBanner("VIP item is already in cart.", "warning");
        return;
      }

      addVip(item);
      showBanner("VIP item added to cart", "success");
      return;
    }
  };

  const isInCart = (item) => {
    if (item.category === "flower") {
      return selectedFlowers.some(f => f._id === item._id);
    }

    if (item.category === "accessory") {
      return selectedAccessories.some(a => a._id === item._id);
    }

    if (item.category === "vip") {
      return selectedVips.some(v => v._id === item._id);
    }

    return false;
  };


  return (
    <div className="space-y-6">
      <Banner type={banner.type} message={banner.message} />

      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold mb-4">My Wishlist</h2>
        {wishlist.length > 0 && (
          <button
            onClick={() => {
              clearWishlist();
            }}
            className="ml-auto px-4 py-2 bg-red-600 text-white font-semibold rounded-lg shadow hover:bg-red-700 transition"
          >
            Clear Wishlist
          </button>
        )}
      </div>

      {wishlist.length === 0 ? (
        <div className="text-gray-500 flex flex-col items-center py-10">
          <HeartOff size={36} className="mb-3" />
          <p className="text-center">No items in your wishlist yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {wishlist.map((item) => (
            <div
              key={item._id}
              className="bg-white border rounded-lg p-4 shadow-sm flex gap-4 relative flex-row flex-wrap sm:flex-nowrap"
            >
              {item.vip && (
                <span className="absolute top-4 right-4 bg-yellow-300 text-yellow-900 px-2 py-0.5 rounded-full text-xs shadow">
                  VIP
                </span>
              )}

              <img
                src={item.image}
                alt={item.name}
                className="h-24 w-24 object-cover rounded-md"
              />

              <div className="flex flex-col justify-between flex-1">
                <div>
                  <h3 className="text-lg font-semibold">{item.name}</h3>
                  <p className="text-gray-600 text-sm mt-1">
                    ${Number(item.price).toFixed(2)}
                  </p>
                </div>

                <div className="mt-4 flex gap-4 flex-row flex-wrap items-center">
                  <button
                    onClick={() => {
                      removeFromWishlist(item._id);
                      showBanner("Removed from wishlist", "error");
                    }}
                    className="text-sm text-red-600 hover:underline flex items-center gap-1"
                  >
                    <Trash2 size={16} />
                    Remove
                  </button>

                  <button
                    onClick={() => handleAddToCart(item)}
                    disabled={isInCart(item)}
                    className={`text-sm px-3 py-1 rounded flex items-center gap-1 transition ${
                      isInCart(item)
                        ? "bg-gray-400 text-white cursor-not-allowed"
                        : "text-blue-600 hover:underline"
                    }`}
                  >
                    <ShoppingCart size={14} />
                    {isInCart(item) ? "In Cart" : "Add to Cart"}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Wishlist;
